import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { X, TrendingUp, Users, Calendar, Activity } from 'lucide-react';

export default function TemplateStatsModal({ isOpen, template, onClose }) {
  const { getAuthenticatedApi } = useAuth();
  const { isDark } = useTheme();
  const api = getAuthenticatedApi();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && template) {
      fetchStats();
    }
  }, [isOpen, template]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/gym_workout_templates.php?action=usage_stats&id=${template.id}`);
      
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Errore nel caricamento statistiche:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">
                📊 Statistiche Template
              </h2>
              <p className="text-white/80 text-sm mt-1">
                {template.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-600 dark:text-white/65">Caricamento statistiche...</span>
            </div>
          ) : stats ? (
            <>
              {/* Statistiche Principali */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <StatCard
                  icon={<Activity className="h-6 w-6" />}
                  label="Utilizzi Totali"
                  value={stats.stats.total_uses || 0}
                  color="blue"
                />
                <StatCard
                  icon={<Users className="h-6 w-6" />}
                  label="Utenti Unici"
                  value={stats.stats.unique_users || 0}
                  color="green"
                />
                <StatCard
                  icon={<Calendar className="h-6 w-6" />}
                  label="Primo Utilizzo"
                  value={formatDate(stats.stats.first_use)}
                  color="purple"
                  small
                />
                <StatCard
                  icon={<TrendingUp className="h-6 w-6" />}
                  label="Ultimo Utilizzo"
                  value={formatDate(stats.stats.last_use)}
                  color="orange"
                  small
                />
              </div>

              {/* Utilizzi per Mese */}
              {stats.monthly_usage && stats.monthly_usage.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    📈 Utilizzi per Mese
                  </h3>
                  <div className="space-y-2">
                    {stats.monthly_usage.map((month) => (
                      <div key={month.month} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <span className="text-gray-700 dark:text-white/85">
                          {new Date(month.month + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                        </span>
                        <div className="flex items-center">
                          <div className="h-2 bg-indigo-600 rounded-full mr-3" style={{ width: `${month.uses_count * 10}px`, maxWidth: '200px' }}></div>
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {month.uses_count}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ultimi Utilizzi */}
              {stats.recent_uses && stats.recent_uses.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    🕒 Ultimi Utilizzi
                  </h3>
                  <div className="space-y-2">
                    {stats.recent_uses.map((use, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {use.scheda_name || 'Scheda senza nome'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-white/65">
                            Assegnata a: <span className="font-medium">{use.assigned_to_name || '-'}</span>
                            {' • '}
                            da: <span className="font-medium">{use.used_by_name}</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600 dark:text-white/65">
                            {formatDateTime(use.used_at)}
                          </p>
                          {use.weights_customized && (
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                              Pesi personalizzati
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stats.stats.total_uses === 0 && (
                <div className="text-center py-12">
                  <Activity className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-white/65">
                    Questo template non è ancora stato utilizzato
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-white/65">
                Nessuna statistica disponibile
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-white/10 px-6 py-4 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-white/85 hover:bg-gray-300 dark:hover:brightness-110 rounded-lg transition-colors"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Componente StatCard
function StatCard({ icon, label, value, color, small = false }) {
  const colors = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-white/10 p-4">
      <div className={`p-2 rounded-lg inline-block mb-2 ${colors[color]}`}>
        {icon}
      </div>
      <p className="text-xs text-gray-500 dark:text-white/65 mb-1">{label}</p>
      <p className={`font-bold text-gray-900 dark:text-white ${small ? 'text-sm' : 'text-xl'}`}>
        {value}
      </p>
    </div>
  );
}












